import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { extractErrorMessage } from 'views/Admin/_shared/errorMessage';
import { UsageWorkspace } from 'views/Admin/UsageReport/types';
import AuditLogFilters from './AuditLogFilters';
import AuditLogList from './AuditLogList';
import { useAuditLog } from './useAuditLog';
import {
  AuditLogFilters as Filters,
  AuditLogSort,
  DEFAULT_AUDIT_FILTERS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
  PageSize,
} from './types';
import './AuditLog.scss';

const AuditLog: React.FC = () => {
  const { t } = useTranslation('admin');
  const dispatch = useDispatch();

  const workspaces = useSelector(
    (state: ApplicationState) => state.blazeUserConfig.blazeUserConfig?.user?.workspaces ?? [],
  );
  const usageWorkspaces: UsageWorkspace[] = useMemo(() => workspaces.map(w => ({ id: w.id, name: w.name })), [
    workspaces,
  ]);

  const [filters, setFilters] = useState<Filters>(DEFAULT_AUDIT_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<AuditLogSort>(DEFAULT_SORT);
  const [refreshKey, setRefreshKey] = useState(0);

  const { rows, total, isLoading, error } = useAuditLog(filters, page, pageSize, sort, refreshKey);

  useEffect(() => {
    if (error) {
      dispatch(showToastNotification(ToastType.Danger, t('auditLog.loadError'), extractErrorMessage(error)));
    }
  }, [error, dispatch, t]);

  const handleFiltersChange = useCallback((next: Filters) => {
    setFilters(next);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((next: number) => {
    setPage(next);
  }, []);

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next as PageSize);
    setPage(0);
  }, []);

  const handleSortChange = useCallback((next: AuditLogSort) => {
    setSort(next);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return (
    <section className="admin-page audit-log-page">
      <h1>{t('pages.auditLog.title')}</h1>
      <p className="admin-page-subtitle">{t('pages.auditLog.subtitle')}</p>

      <AuditLogFilters
        filters={filters}
        workspaces={usageWorkspaces}
        isRefreshing={isLoading}
        onChange={handleFiltersChange}
        onRefresh={handleRefresh}
      />

      {isLoading && rows.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SimpleLoader />
        </div>
      ) : (
        <AuditLogList
          rows={rows}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          total={total}
          sort={sort}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSortChange}
        />
      )}
    </section>
  );
};

export default AuditLog;
